import { useEffect, useState } from 'react';
import { useEnv } from 'src/context/EnvProvider';
import { Assembly } from 'src/services/assemblies';
import { loadAndConfigureTexture, createRectangleShape } from 'src/utils/common.utils';
import { getDefaultTextureSetup } from 'src/utils/piece';
import { configureHDTexture } from 'src/utils/textureLoader';
import { changeSteleShape, getSteleExtrudeSettings } from 'src/utils/stele.utils';
import * as THREE from 'three';
import { Shape, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import {
  makeExtrudeSettings,
  PatternsMap,
  populatePatternsForEntity,
} from './useConfigurator.utils';

/**
 * Hook qui gère l'initialisation de l'assemblage :
 * - Charge les textures et prépare les pièces actives
 * - Formate les stèles, placages, semelles et coffrets
 * - Peuple les patterns pour toutes les entités
 */
export const useAssemblyInit = (assembly: Assembly | null) => {
  const { buckets } = useEnv();

  const [customSteles, setCustomSteles] = useState<any[]>([]);
  const [customPlatings, setCustomPlatings] = useState<any[]>([]);
  const [customSoles, setCustomSoles] = useState<any[]>([]);
  const [customBoxes, setCustomBoxes] = useState<any[]>([]);
  const [activePieces, setActivePieces] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<PatternsMap>({});

  useEffect(() => {
    const loader = new THREE.TextureLoader();
    const bucketUrl = buckets?.BUCKET_3D_PIECES || '';

    // --- Pièces d'assemblage ---
    const assemblyPieces = assembly?.pieces?.map((el) => {
      const loadedTexture = loadAndConfigureTexture(
        loader,
        bucketUrl,
        el.granit?.image,
        configureHDTexture,
        {
          repeatX: 1,
        },
      );

      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.repeat.x = 1;

      const defaultPatterns = getDefaultTextureSetup(el.piece, buckets);

      return {
        uuid: generateUUID(),
        ...el.piece,
        position: el.position,
        newGeometry: el.newGeometry,
        defaultPatterns,
        texture: loadedTexture,
        granit: el.granit,
        fabrications: el.piece.pieceFabrication?.map(
          (el: any) => el?.fabrication,
        ),
        patterns: el.patterns,
        isBlocked: el.isBlocked,
      };
    });

    // --- Patterns des pièces d'assemblage ---
    const newPatterns: PatternsMap = { ...patterns };

    if (assemblyPieces) {
      for (const element of assemblyPieces) {
        populatePatternsForEntity(
          newPatterns,
          element.uuid,
          element.patterns,
          bucketUrl,
        );
      }
    }

    // --- Pièces de sous-assemblages ---
    const underAssembliesPieces = [];

    if (assembly?.underAssemblyOnAssembly) {
      for (const el of assembly.underAssemblyOnAssembly) {
        const underAssembly = {
          id: el.underAssembly.id,
          uuid: generateUUID(),
          name: el.underAssembly.name,
          modifications: el.underAssembly.modificationOnUnderAssembly.map(
            (element) => {
              return { ...element.modification, type: element.type };
            },
          ),
          fabrications: el.underAssembly.UnderAssemblyFabrication.map(
            (fabrication) => fabrication.fabrication,
          ),
        };
        for (const element of el.underAssembly.pieceOnUnderAssembly) {
          const underAssemblyOnAssemblyDetailsPiece =
            el.underAssemblyOnAssemblyDetailsPiece.find((details) => {
              if (details.pieceOnUnderAssemblyId === element.id) return true;
              return false;
            });
          const loadedTexture = loadAndConfigureTexture(
            loader,
            bucketUrl,
            underAssemblyOnAssemblyDetailsPiece?.granit?.image ?? '',
            configureHDTexture,
            {
              repeatX: 1,
            },
          );
          const defaultPatterns = getDefaultTextureSetup(
            element.piece,
            buckets,
          );

          const piece = {
            uuid: generateUUID(),
            ...element.piece,
            defaultPatterns,
            isBlocked: underAssemblyOnAssemblyDetailsPiece?.isBlocked ?? false,
            position: underAssemblyOnAssemblyDetailsPiece?.position,
            underAssembly,
            newGeometry: underAssemblyOnAssemblyDetailsPiece?.newGeometry,
            pieceOnUnderAssemblyId: element.id,
            granit: underAssemblyOnAssemblyDetailsPiece?.granit,
            texture: loadedTexture,
            fabrications: element.piece.pieceFabrication?.map(
              (el: any) => el?.fabrication,
            ),
          };

          if (underAssemblyOnAssemblyDetailsPiece) {
            populatePatternsForEntity(
              newPatterns,
              piece.uuid,
              underAssemblyOnAssemblyDetailsPiece.patterns,
              bucketUrl,
            );
          }
          underAssembliesPieces.push(piece);
        }
      }
    }

    const activesPieces = [...underAssembliesPieces, ...(assemblyPieces ?? [])];
    setActivePieces(activesPieces);

    // --- Stèles ---
    const formatedSteles = assembly?.steles.map((steleOnAssembly) => {
      const loadedTexture = loadAndConfigureTexture(
        loader,
        bucketUrl,
        steleOnAssembly.granit?.image ?? '',
        configureHDTexture,
        {
          repeatX: 0.5,
          repeatY: 0.5,
        },
      );

      const shape = new Shape();
      const extrudeSettings = getSteleExtrudeSettings(
        steleOnAssembly.stele.depth,
      );
      changeSteleShape(
        shape,
        steleOnAssembly.stele.boxLeftHeight,
        steleOnAssembly.stele.boxRightHeight,
        steleOnAssembly.stele.boxHeight,
        steleOnAssembly.stele.boxUpWidth,
        steleOnAssembly.stele.boxDownWidth,
        steleOnAssembly.stele.headIndex,
        steleOnAssembly.stele.baseIndex,
      );

      return {
        id: generateUUID(),
        texture: loadedTexture,
        granit: steleOnAssembly.granit,
        boxLeftHeight: steleOnAssembly.stele.boxLeftHeight,
        boxRightHeight: steleOnAssembly.stele.boxRightHeight,
        boxHeight: steleOnAssembly.stele.boxHeight,
        boxUpWidth: steleOnAssembly.stele.boxUpWidth,
        boxDownWidth: steleOnAssembly.stele.boxDownWidth,
        headIndex: steleOnAssembly.stele.headIndex,
        baseIndex: steleOnAssembly.stele.baseIndex,
        depth: steleOnAssembly.stele.depth,
        isBlocked: steleOnAssembly.isBlocked,
        shape,
        extrudeSettings,
        position: new Vector3(
          steleOnAssembly.position.x,
          steleOnAssembly.position.y,
          steleOnAssembly.position.z,
        ),
        patterns: steleOnAssembly.patterns,
        additionalPriceForCustomStele:
          steleOnAssembly.additionalPriceForCustomStele,
      };
    });

    if (formatedSteles) {
      for (const element of formatedSteles) {
        populatePatternsForEntity(
          newPatterns,
          element.id,
          element.patterns,
          bucketUrl,
        );
      }
    }

    setCustomSteles(formatedSteles ?? []);

    // --- Placages ---
    const formatedPlating = assembly?.platings.map((platingOnAssembly) => {
      if (!platingOnAssembly.plating) return platingOnAssembly;
      const loadedTexture = loadAndConfigureTexture(
        loader,
        bucketUrl,
        platingOnAssembly.granit?.image ?? '',
        configureHDTexture,
        {
          repeatX: 0.5,
          repeatY: 0.5,
        },
      );

      const shape = new Shape();
      const shapeDetails = {
        display: true,
        hasSlope: false,
        isRotated: platingOnAssembly.plating.isRotated,
        depth: platingOnAssembly.plating.depth,
        length: platingOnAssembly.plating.length,
        front: platingOnAssembly.plating.front,
        back: platingOnAssembly.plating.back ?? undefined,
        monumentWidth: platingOnAssembly.plating.monumentWidth,
        monumentLength: platingOnAssembly.plating.monumentLength,
        monumentFront: platingOnAssembly.plating.monumentFront ?? undefined,
        monumentBack: platingOnAssembly.plating.monumentBack ?? undefined,
        mode: platingOnAssembly.plating.mode,
      };

      createRectangleShape(
        shape,
        platingOnAssembly.plating.length,
        shapeDetails.front,
        shapeDetails.back,
      );

      return {
        id: generateUUID(),
        isActive: platingOnAssembly.plating.isActive,
        texture: loadedTexture,
        granit: platingOnAssembly.granit,
        isBlocked: platingOnAssembly.isBlocked,
        key: platingOnAssembly.plating.key,
        shape,
        shapeDetails,
        extrudeSettings: makeExtrudeSettings(platingOnAssembly.plating.depth),
        position: new Vector3(
          platingOnAssembly.position.x,
          platingOnAssembly.position.y,
          platingOnAssembly.position.z,
        ),
      };
    });

    if (formatedPlating) setCustomPlatings(formatedPlating);

    // --- Semelles ---
    const formatedSole = assembly?.soles.map((soleOnAssembly) => {
      if (!soleOnAssembly.sole) return soleOnAssembly;

      const loadedTexture = loadAndConfigureTexture(
        loader,
        bucketUrl,
        soleOnAssembly.granit?.image ?? '',
        configureHDTexture,
        {
          repeatX: 0.5,
          repeatY: 0.5,
        },
      );

      const shape = new Shape();
      const shapeDetails = {
        display: true,
        isRotated: soleOnAssembly.sole.isRotated,
        length: soleOnAssembly.sole.length,
        height: soleOnAssembly.sole.height,
        monumentWidth: soleOnAssembly.sole.monumentWidth,
        monumentLength: soleOnAssembly.sole.monumentLength,
        holeWidth: soleOnAssembly.sole.holeWidth,
        holeLength: soleOnAssembly.sole.holeLength,
        poseX: soleOnAssembly.sole.poseX,
        poseZ: soleOnAssembly.sole.poseZ,
        frontDepth: soleOnAssembly.sole.frontDepth,
        depth: soleOnAssembly.sole.depth,
        holeMode: soleOnAssembly.sole.holeMode,
        mode: soleOnAssembly.sole.mode,
        orientation: soleOnAssembly.sole.orientation,
        hasCustomFrontDepth: soleOnAssembly.sole.hasCustomFrontDepth,
      };

      createRectangleShape(
        shape,
        soleOnAssembly.sole.length,
        shapeDetails.height,
      );

      return {
        id: generateUUID(),
        isActive: soleOnAssembly.sole.isActive,
        texture: loadedTexture,
        granit: soleOnAssembly.granit,
        isBlocked: soleOnAssembly.isBlocked,
        key: soleOnAssembly.sole.key,
        shape,
        shapeDetails,
        position: new Vector3(
          soleOnAssembly.position.x,
          soleOnAssembly.position.y,
          soleOnAssembly.position.z,
        ),
        extrudeSettings: makeExtrudeSettings(soleOnAssembly.sole.depth),
      };
    });

    if (formatedSole) setCustomSoles(formatedSole);

    // --- Coffrets ---
    const formatedBox = assembly?.boxes?.map((boxOnAssembly: any) => {
      if (!boxOnAssembly.box) return boxOnAssembly;

      const loadedTexture = loadAndConfigureTexture(
        loader,
        bucketUrl,
        boxOnAssembly.granit?.image ?? '',
        configureHDTexture,
        {
          repeatX: 0.5,
          repeatY: 0.5,
        },
      );

      const shape = new Shape();
      const shapeDetails = {
        display: true,
        hasSlope: boxOnAssembly.box.hasSlope ?? false,
        isRotated: boxOnAssembly.box.isRotated,
        depth: boxOnAssembly.box.depth,
        length: boxOnAssembly.box.length,
        front: boxOnAssembly.box.front,
        back: boxOnAssembly.box.back ?? undefined,
        monumentWidth: boxOnAssembly.box.monumentWidth,
        monumentLength: boxOnAssembly.box.monumentLength,
        monumentFront: boxOnAssembly.box.monumentFront ?? undefined,
        monumentBack: boxOnAssembly.box.monumentBack ?? undefined,
        mode: boxOnAssembly.box.mode,
        orientation: boxOnAssembly.box.orientation,
      };

      createRectangleShape(
        shape,
        boxOnAssembly.box.length,
        shapeDetails.front,
        shapeDetails.back,
      );

      return {
        id: generateUUID(),
        isActive: boxOnAssembly.box.isActive,
        texture: loadedTexture,
        granit: boxOnAssembly.granit,
        isBlocked: boxOnAssembly.isBlocked,
        key: boxOnAssembly.box.key,
        shape,
        shapeDetails,
        position: new Vector3(
          boxOnAssembly.position.x,
          boxOnAssembly.position.y,
          boxOnAssembly.position.z,
        ),
        extrudeSettings: makeExtrudeSettings(boxOnAssembly.box.depth),
      };
    });

    if (formatedBox) setCustomBoxes(formatedBox);

    setPatterns(newPatterns);
  }, []);

  return {
    customSteles,
    setCustomSteles,
    customPlatings,
    setCustomPlatings,
    customSoles,
    setCustomSoles,
    customBoxes,
    setCustomBoxes,
    activePieces,
    setActivePieces,
    patterns,
    setPatterns,
  };
};
